import React, { useState, useEffect, useRef } from 'react';
import { loadGoogleMapsAPI, isGoogleMapsReady, getRealAddressFromCoordinates } from '../../services/googleMapsService';
import { getRandomAccommodationData, getRandomCharges } from '../../data/randomAccommodationData';
import { getParcLogements } from '../../services/logementService';

const MapSelector = ({
  schoolAddress,
  schoolCoordinates, // Format: "long:X,lat:Y,alt:Z"
  onAccommodationSelect,
  selectedAccommodationId,
  className = ""
}) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [schoolMarker, setSchoolMarker] = useState(null);
  const [accommodationMarkers, setAccommodationMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [isCreatingAccommodation, setIsCreatingAccommodation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  // États pour le basculement entre logements générés et parc
  const [viewMode, setViewMode] = useState('generated'); // 'generated' ou 'parc'
  const [parcLogements, setParcLogements] = useState([]);
  const [loadingParc, setLoadingParc] = useState(false);

  // Styles de carte professionnels bleu/orange soft
  const mapStyles = [
    { elementType: "geometry", stylers: [{ color: "#f8fafc" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#64748b" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#f8fafc" }] },
    { featureType: "administrative.land_parcel", elementType: "labels.text.fill", stylers: [{ color: "#cbd5e1" }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: "#f1f5f9" }] },
    { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#ecfdf5" }] },
    { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e2e8f0" }] },
    { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
    { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
    { featureType: "transit.line", elementType: "geometry", stylers: [{ color: "#e2e8f0" }] },
    { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#f1f5f9" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#dbeafe" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3b82f6" }] }
  ];

  // Initialiser la carte
  useEffect(() => {
    const initMap = async () => {
      try {
        setIsLoading(true);
        setError(null);

        await loadGoogleMapsAPI();

        if (!isGoogleMapsReady()) {
          throw new Error('Services Google Maps non disponibles');
        }

        // Créer la carte
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          zoom: 14,
          center: { lat: 48.8566, lng: 2.3522 }, // Paris par défaut
          styles: mapStyles,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          gestureHandling: 'cooperative'
        });

        setMap(mapInstance);

        // Créer InfoWindow réutilisable
        const infoWindowInstance = new window.google.maps.InfoWindow({
          disableAutoPan: true, // Empêcher le mouvement automatique de la carte
          maxWidth: 320
        });
        setInfoWindow(infoWindowInstance);

      } catch (err) {
        console.error('Erreur initialisation carte:', err);
        setError('Impossible de charger la carte. Vérifiez votre connexion.');
      } finally {
        setIsLoading(false);
      }
    };

    initMap();
  }, []);

  // Positionner l'école et générer les logements quand les coordonnées sont disponibles
  useEffect(() => {
    if (!map || !schoolCoordinates) return;

    positionSchoolAndGenerateAccommodations();
  }, [map, schoolCoordinates]);

  // Gérer la sélection externe - Simplifié pour éviter les conflits
  useEffect(() => {
    // Gérer uniquement la sélection initiale en mode édition ou sélection depuis l'extérieur
    if (selectedAccommodationId && accommodationMarkers.length > 0 && !selectedMarker) {
      console.log('Recherche marqueur initial avec ID:', selectedAccommodationId);
      const marker = accommodationMarkers.find(m => m.accommodationData.id === selectedAccommodationId);
      if (marker) {
        console.log('Marqueur initial trouvé, sélection...');
        setSelectedMarker(marker);
        updateMarkerAppearance(marker, true, false);
        showInfoWindow(marker);
      }
    }
  }, [selectedAccommodationId, accommodationMarkers]);

  const parseCoordinates = (coordString) => {
    if (!coordString) return null;

    try {
      // Format: "long:X,lat:Y,alt:Z"
      const parts = coordString.split(',');
      if (parts.length >= 2) {
        const lng = parseFloat(parts[0].replace('long:', ''));
        const lat = parseFloat(parts[1].replace('lat:', ''));
        return { lat, lng };
      }
    } catch (error) {
      console.warn('Erreur parsing coordonnées:', error);
    }

    return null;
  };

  const loadParcLogements = async () => {
    try {
      setLoadingParc(true);
      console.log('🏠 Chargement des logements du parc...');
      const parcData = await getParcLogements();

      // Transformer les données du parc pour être compatibles avec l'affichage
      const transformedParc = parcData.map(logement => ({
        id: logement.id,
        title: logement.titre,
        description: logement.description,
        price: logement.loyer,
        charges: logement.montant_charges,
        address: logement.adresse,
        ville: logement.ville,
        pays: logement.pays,
        code_postal: logement.code_postal,
        coordinates: logement.coordonnees_gps,
        // Calculer distance si coordonnées disponibles
        distanceKm: '?', // On calculera plus tard si nécessaire
        isExisting: true,
        author: logement.author
      }));

      setParcLogements(transformedParc);
      console.log(`✅ ${transformedParc.length} logements du parc chargés`);
    } catch (error) {
      console.error('Erreur chargement parc logements:', error);
      setErrorMessage('Impossible de charger les logements du parc.');
      setShowErrorModal(true);
    } finally {
      setLoadingParc(false);
    }
  };

  const positionSchoolAndGenerateAccommodations = async () => {
    try {
      setIsLoading(true);

      // Parser les coordonnées de l'école
      const schoolPosition = parseCoordinates(schoolCoordinates);

      if (!schoolPosition) {
        throw new Error('Coordonnées de l\'école invalides');
      }

      // Centrer la carte sur l'école
      map.setCenter(schoolPosition);
      map.setZoom(15);

      // Créer le marqueur de l'école
      const schoolMarkerInstance = new window.google.maps.Marker({
        position: schoolPosition,
        map: map,
        title: 'École/Université',
        icon: {
          url: 'data:image/svg+xml,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="15" fill="#ef4444" stroke="#ffffff" stroke-width="2"/>
              <text x="16" y="21" text-anchor="middle" fill="white" font-size="16" font-weight="bold">🎓</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32)
        },
        zIndex: 1000
      });

      setSchoolMarker(schoolMarkerInstance);

      // Générer les logements aléatoires autour de l'école
      await generateRandomAccommodations(schoolPosition);

    } catch (error) {
      console.error('Erreur positionnement école:', error);
      setError('Impossible de localiser l\'école sur la carte.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateRandomAccommodations = async (schoolPosition) => {
    const markers = [];
    const minDistance = 100; // 100m minimum
    const maxDistance = 100000; // 100km maximum
    const targetCount = 100;

    console.log('🚀 Génération de logements avec adresses réelles...');

    // Générer des candidats avec plus d'options pour obtenir des adresses réelles
    const maxCandidates = 200;
    let successfulMarkers = 0;

    for (let i = 0; i < maxCandidates && successfulMarkers < targetCount; i++) {
      const candidate = generateRandomPoint(schoolPosition, maxDistance);
      const distance = calculateDistance(schoolPosition, candidate);

      if (distance >= minDistance && distance <= maxDistance) {
        try {
          // Obtenir l'adresse réelle via reverse geocoding
          const realAddressData = await getRealAddressFromCoordinates(candidate.lat, candidate.lng);

          if (realAddressData && realAddressData.formatted_address) {
            // Créer les données d'accommodation avec adresse réelle
            const accommodationData = {
              id: `map_accommodation_${successfulMarkers}`,
              // ✅ Données fictives (datasets)
              ...getRandomAccommodationData(),
              charges: getRandomCharges(),
              // ✅ Données réelles (Google Geocoding)
              address: realAddressData.formatted_address,
              ville: realAddressData.city,
              pays: realAddressData.country,
              code_postal: realAddressData.postal_code,
              position: candidate,
              distance: Math.round(distance),
              distanceKm: (distance / 1000).toFixed(1),
              coordinates: `long:${candidate.lng},lat:${candidate.lat},alt:0`
            };

            const marker = createAccommodationMarker(accommodationData);
            markers.push(marker);
            successfulMarkers++;

            //console.log(`✅ Logement ${successfulMarkers}/100: ${realAddressData.formatted_address}`);
          }
        } catch (error) {
          console.warn('Erreur geocoding pour position:', candidate, error);
        }
      }
    }

    console.log(`🎯 Génération terminée: ${successfulMarkers} logements créés`);
    setAccommodationMarkers(markers);

    // Ajuster la vue pour inclure tous les marqueurs
    if (markers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(schoolPosition);
      markers.forEach(marker => bounds.extend(marker.getPosition()));
      map.fitBounds(bounds);

      // Assurer un zoom minimum pour la visibilité
      const listener = window.google.maps.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        window.google.maps.event.removeListener(listener);
      });
    }
  };

  // Fonctions utilitaires maintenues pour compatibilité

  const generateRandomPoint = (center, radius) => {
    const x0 = center.lng;
    const y0 = center.lat;
    const rd = radius / 111300; // Conversion mètres vers degrés
    const u = Math.random();
    const v = Math.random();
    const w = rd * Math.sqrt(u);
    const t = 2 * Math.PI * v;
    const x = w * Math.cos(t);
    const y = w * Math.sin(t);
    const xp = x / Math.cos(y0 * Math.PI / 180); // Correction longitude

    return {
      lat: y + y0,
      lng: xp + x0
    };
  };

  const calculateDistance = (pos1, pos2) => {
    const R = 6371000; // Rayon terre en mètres
    const dLat = (pos2.lat - pos1.lat) * Math.PI / 180;
    const dLng = (pos2.lng - pos1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(pos1.lat * Math.PI / 180) * Math.cos(pos2.lat * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };


  const createAccommodationMarker = (accommodationData) => {
    const marker = new window.google.maps.Marker({
      position: accommodationData.position,
      map: map,
      title: accommodationData.title,
      icon: {
        url: 'data:image/svg+xml,' + encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="11" fill="#3b82f6" stroke="#ffffff" stroke-width="2"/>
            <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">🏠</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(24, 24)
      },
      zIndex: 100
    });

    marker.accommodationData = accommodationData;
    marker.isSelected = false; // Initialiser l'état de sélection

    // Événements pour le hover
    marker.addListener('mouseover', () => handleMarkerHover(marker, true));
    marker.addListener('mouseout', () => handleMarkerHover(marker, false));

    // Événement pour la sélection
    marker.addListener('click', () => handleMarkerClick(marker));

    return marker;
  };

  const handleMarkerHover = (marker, isHovering) => {
    console.log('HOVER EVENT:', {
      markerId: marker.accommodationData?.id,
      isHovering,
      isSelected: marker.isSelected,
      selectedMarkerId: selectedMarker?.accommodationData?.id
    });

    // Vérifier si le marqueur est marqué comme sélectionné
    if (marker.isSelected) {
      console.log('Marqueur sélectionné - IGNORER le hover');
      return;
    }

    if (isHovering) {
      setHoveredMarker(marker);
      updateMarkerAppearance(marker, false, true);

      // Afficher un tooltip HTML léger au lieu de l'InfoWindow
      const data = marker.accommodationData;
      setTooltip({
        show: true,
        content: `${data.title} - ${data.price}€/mois`,
        x: 0, // Position sera gérée par CSS
        y: 0
      });
    } else {
      setHoveredMarker(null);
      updateMarkerAppearance(marker, false, false);
      setTooltip({ show: false, content: '', x: 0, y: 0 });
    }
  };


  const handleMarkerClick = async (marker, fromGridCard = false) => {
    console.log('=== CLIC MARQUEUR ===', marker.accommodationData?.id);
    console.log('État avant clic:', marker.isSelected);
    console.log('Clic depuis grid card:', fromGridCard);

    try {
      // IMPORTANT: Capturer l'état AVANT le reset
      const wasAlreadySelected = marker.isSelected;
      console.log('📋 Marqueur était sélectionné:', wasAlreadySelected);

      // ÉTAPE 1: RESET COMPLET - Remettre TOUS les marqueurs à l'état normal
      console.log('🔄 RESET COMPLET de tous les marqueurs...');
      console.log('👆 Marqueur cliqué ID:', marker.accommodationData?.id);
      accommodationMarkers.forEach((m, index) => {
        const isClickedMarker = m === marker;
        console.log(`Reset marqueur ${index} (${m.accommodationData?.id}): ${m.isSelected} → false ${isClickedMarker ? '← MARQUEUR CLIQUÉ' : ''}`);
        m.isSelected = false;
        updateMarkerAppearance(m, false, false);

        // Vérification supplémentaire pour le marqueur cliqué
        if (isClickedMarker) {
          console.log('🔍 VÉRIFICATION marqueur cliqué après updateMarkerAppearance:', {
            id: m.accommodationData?.id,
            isSelected: m.isSelected,
            'marker === clicked': m === marker
          });
        }
      });

      // Fermer toutes les InfoWindows
      infoWindow?.close();
      setSelectedMarker(null);

      // Si on clique sur un marqueur qui était déjà sélectionné, s'arrêter ici (désélection complète)
      if (wasAlreadySelected) {
        console.log('🔴 DÉSÉLECTION COMPLÈTE - Le marqueur était déjà sélectionné');

        // Notifier le parent de la désélection
        if (onAccommodationSelect) {
          onAccommodationSelect(null);
        }
        return;
      }

      // ÉTAPE 2: SÉLECTION du nouveau marqueur
      console.log('✅ SÉLECTION du nouveau marqueur:', marker.accommodationData?.id);
      marker.isSelected = true;
      setSelectedMarker(marker);
      updateMarkerAppearance(marker, true, false);
      showInfoWindow(marker);

      // Si c'est un clic depuis la grid card, scroll vers la carte APRÈS la sélection
      if (fromGridCard && mapRef.current) {
        console.log('🔄 Scroll automatique vers la carte après sélection...');
        // Petit délai pour laisser le temps à l'InfoWindow de s'afficher
        setTimeout(() => {
          mapRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }, 100);
      }

      // ÉTAPE 3: NOTIFIER IMMÉDIATEMENT le parent (sélection visuelle)
      console.log('🔗 Notification immédiate du parent...');
      if (onAccommodationSelect) {
        onAccommodationSelect({
          ...marker.accommodationData,
          // Utiliser l'ID temporaire pour l'instant
          id: marker.accommodationData.id || `temp_${Date.now()}`,
          isExisting: false,
          isPending: true  // Indiquer que l'API est en cours
        });
      }

      // ÉTAPE 4: Vérifier et créer le logement en arrière-plan
      console.log('🏠 Début création/vérification...');
      await handleAccommodationSelection(marker.accommodationData, marker);

      // ÉTAPE 4: S'assurer que le marqueur reste sélectionné après l'opération
      console.log('🔒 Confirmation sélection finale...');
      updateMarkerAppearance(marker, true, false);

    } catch (error) {
      console.error('Erreur lors de la sélection du logement:', error);
      setErrorMessage('Une erreur est survenue lors de la sélection du logement.');
      setShowErrorModal(true);

      // En cas d'erreur, reset complet
      accommodationMarkers.forEach(m => {
        m.isSelected = false;
        updateMarkerAppearance(m, false, false);
      });
      setSelectedMarker(null);
      infoWindow?.close();
    }
  };

  const handleAccommodationSelection = async (accommodationData, marker) => {
    try {
      setIsCreatingAccommodation(true);

      // Récupérer l'ID de l'utilisateur automation pour les créations automatiques
      let automationUserId = null;
      try {
        const automationResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/logements/automation-user-id`);
        if (automationResponse.ok) {
          const automationData = await automationResponse.json();
          automationUserId = automationData.automation_user_id;
        }
      } catch (error) {
        console.warn('Impossible de récupérer l\'ID automation, utilisation du défaut:', error);
      }

      // Vérifier si le logement existe déjà
      const checkResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/logements/check-existence?adresse=${encodeURIComponent(accommodationData.address)}&ville=${encodeURIComponent(accommodationData.ville)}`
      );

      if (!checkResponse.ok) {
        throw new Error('Erreur lors de la vérification du logement');
      }

      const checkResult = await checkResponse.json();

      if (checkResult.exists) {
        // Le logement existe déjà
        setSuccessMessage(`Logement "${accommodationData.title}" sélectionné avec succès !`);
        setShowSuccessModal(true);

        // Mettre à jour avec l'ID réel du logement existant
        if (onAccommodationSelect) {
          onAccommodationSelect({
            ...accommodationData,
            id: checkResult.logement.id,
            isExisting: true,
            isPending: false  // API terminée
          });
        }
      } else {
        // Créer le nouveau logement
        const newLogement = {
          titre: accommodationData.title,
          description: accommodationData.description,
          adresse: accommodationData.address,
          ville: accommodationData.ville,
          pays: accommodationData.pays,
          code_postal: accommodationData.code_postal,
          loyer: accommodationData.price,
          montant_charges: accommodationData.charges,
          coordonnees_gps: accommodationData.coordinates,
          cood_gps_standart: `${accommodationData.position.lat},${accommodationData.position.lng}`,
          statut: 'disponible',
          author_id: automationUserId // Utiliser l'utilisateur automation pour les créations automatiques
        };

        // Construire l'URL avec current_user_id si on a un automationUserId
        const createUrl = automationUserId
          ? `${process.env.REACT_APP_API_URL}/api/logements/?current_user_id=${automationUserId}`
          : `${process.env.REACT_APP_API_URL}/api/logements/`;

        const createResponse = await fetch(createUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newLogement)
        });

        if (!createResponse.ok) {
          const errorData = await createResponse.json();
          throw new Error(errorData.detail || 'Erreur lors de la création du logement');
        }

        const createdLogement = await createResponse.json();

        setSuccessMessage(`Nouveau logement "${accommodationData.title}" créé et sélectionné avec succès !`);
        setShowSuccessModal(true);

        // IMPORTANT: Mettre à jour l'ID du marqueur avec l'ID réel de la BDD
        marker.accommodationData.id = createdLogement.id;
        console.log('ID marqueur mis à jour:', createdLogement.id);

        // Mettre à jour avec l'ID réel du logement créé
        if (onAccommodationSelect) {
          onAccommodationSelect({
            ...accommodationData,
            id: createdLogement.id,
            isExisting: false,
            isNew: true,
            isPending: false  // API terminée
          });
        }
      }

    } catch (error) {
      console.error('Erreur lors de la gestion du logement:', error);
      setErrorMessage(error.message || 'Une erreur est survenue lors de la création du logement.');
      setShowErrorModal(true);
    } finally {
      setIsCreatingAccommodation(false);
    }
  };

  const handleParcLogementClick = async (logement) => {
    console.log('🏠 Clic logement parc:', logement.id);

    try {
      // Reset les marqueurs de la carte s'il y en a
      accommodationMarkers.forEach(m => {
        m.isSelected = false;
        updateMarkerAppearance(m, false, false);
      });
      setSelectedMarker(null);
      infoWindow?.close();

      // Géocoder l'adresse du logement pour obtenir les coordonnées
      if (logement.address && window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();

        try {
          console.log('🔍 Géocodage de l\'adresse:', logement.address);

          const results = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: logement.address }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results);
              } else {
                reject(new Error(`Géocodage échoué: ${status}`));
              }
            });
          });

          const position = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          };

          console.log('✅ Position trouvée:', position);

          // Créer un marqueur temporaire pour ce logement du parc
          const parcMarker = new window.google.maps.Marker({
            position: position,
            map: map,
            title: logement.title,
            icon: {
              url: 'data:image/svg+xml,' + encodeURIComponent(`
                <svg width="50" height="50" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#10b981" stroke="#ffffff" stroke-width="3"/>
                  <text x="12" y="16" text-anchor="middle" fill="white" font-size="16" font-weight="bold">🏠</text>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(50, 50)
            },
            zIndex: 300
          });

          // Stocker les données du logement avec le marqueur
          parcMarker.accommodationData = {
            ...logement,
            position: position,
            distanceKm: '?'
          };

          // Centrer la carte sur ce marqueur
          map.setCenter(position);
          map.setZoom(16);

          // Afficher l'InfoWindow
          const content = `
            <div style="padding: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 280px;">
              <div style="border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                <h3 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600; color: #1f2937; line-height: 1.2;">
                  ${logement.title}
                </h3>
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                  <span style="background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 2px 8px; border-radius: 12px; font-size: 14px; font-weight: 600;">
                    ${logement.price}€/mois
                  </span>
                  <span style="color: #6b7280; font-size: 12px;">
                    + ${logement.charges}€ charges
                  </span>
                </div>
              </div>

              <div style="margin-bottom: 8px;">
                <p style="margin: 0; font-size: 13px; color: #4b5563; line-height: 1.4;">
                  ${logement.description}
                </p>
              </div>

              <div style="border-top: 1px solid #f3f4f6; padding-top: 8px;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 11px; color: #6b7280;">
                  <div><strong>🏠 Type:</strong> Parc</div>
                  <div><strong>🏙️ Ville:</strong> ${logement.ville}</div>
                  <div style="grid-column: 1 / -1;"><strong>🌍 Pays:</strong> ${logement.pays}</div>
                </div>
              </div>

              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #f3f4f6; text-align: center;">
                <span style="background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 500;">
                  ✅ Logement du parc sélectionné
                </span>
              </div>
            </div>
          `;

          infoWindow.setContent(content);
          infoWindow.open(map, parcMarker);

          // Scroll automatique vers la carte
          console.log('🔄 Scroll automatique vers la carte...');
          setTimeout(() => {
            mapRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }, 100);

        } catch (geocodingError) {
          console.warn('Géocodage échoué, position par défaut:', geocodingError);
          // Même sans géocodage, on continue avec la sélection
        }
      }

      // Notifier la sélection du logement du parc
      if (onAccommodationSelect) {
        onAccommodationSelect({
          ...logement,
          isExisting: true,
          isPending: false
        });
      }

      setSuccessMessage(`Logement "${logement.title}" sélectionné avec succès !`);
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Erreur lors de la sélection du logement parc:', error);

      // Même en cas d'erreur, on continue avec la sélection (sans marqueur)
      if (onAccommodationSelect) {
        onAccommodationSelect({
          ...logement,
          isExisting: true,
          isPending: false
        });
      }

      setSuccessMessage(`Logement "${logement.title}" sélectionné avec succès !`);
      setShowSuccessModal(true);
    }
  };

  const updateMarkerAppearance = (marker, isSelected, isHovered) => {
    console.log('📍 updateMarkerAppearance ENTRÉ:', {
      markerId: marker.accommodationData?.id,
      isSelected,
      isHovered,
      markerIsSelected: marker.isSelected
    });

    let fillColor = '#3b82f6'; // Bleu par défaut
    let strokeColor = '#ffffff';
    let strokeWidth = 2;
    let size = 24;
    let fontSize = 12;
    let zIndex = 100;

    if (isSelected) {
      fillColor = '#f97316'; // Orange vif pour sélection
      strokeColor = '#ffffff';
      strokeWidth = 3; // Bordure plus épaisse
      size = 50; // TRÈS gros pour bien marquer la différence
      fontSize = 16; // Police plus grande aussi
      zIndex = 200;
      console.log('Marqueur sélectionné - ORANGE et TRÈS GROS');
    } else if (isHovered) {
      fillColor = '#60a5fa'; // Bleu plus clair pour hover
      strokeColor = '#ffffff';
      strokeWidth = 2;
      size = 26;
      fontSize = 12;
      zIndex = 150;
      console.log('Marqueur survolé - BLEU CLAIR');
    } else {
      console.log('Marqueur normal - BLEU');
    }

    const newIcon = {
      url: 'data:image/svg+xml,' + encodeURIComponent(`
        <svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>
          <text x="12" y="16" text-anchor="middle" fill="white" font-size="${fontSize}" font-weight="bold">🏠</text>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(size, size)
    };

    console.log(`🎨 Icône mise à jour pour ${marker.accommodationData?.id}:`, {
      size,
      fillColor,
      isSelected,
      isHovered
    });

    marker.setIcon(newIcon);
    marker.setZIndex(zIndex);

    console.log(`✅ updateMarkerAppearance TERMINÉ pour ${marker.accommodationData?.id} - Icône appliquée: ${size}px ${fillColor}`);
  };

  const showInfoWindow = (marker) => {
    if (!infoWindow) return;

    const data = marker.accommodationData;
    const content = `
      <div style="padding: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 280px;">
        <div style="border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
          <h3 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600; color: #1f2937; line-height: 1.2;">
            ${data.title}
          </h3>
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <span style="background: linear-gradient(135deg, #3b82f6, #1d4ed8); color: white; padding: 2px 8px; border-radius: 12px; font-size: 14px; font-weight: 600;">
              ${data.price}€/mois
            </span>
            <span style="color: #6b7280; font-size: 12px;">
              + ${data.charges}€ charges
            </span>
          </div>
        </div>

        <div style="margin-bottom: 8px;">
          <p style="margin: 0; font-size: 13px; color: #4b5563; line-height: 1.4;">
            ${data.description}
          </p>
        </div>

        <div style="border-top: 1px solid #f3f4f6; padding-top: 8px;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 11px; color: #6b7280;">
            <div><strong>📍 Distance:</strong> ${data.distanceKm}km</div>
            <div><strong>🏙️ Ville:</strong> ${data.ville}</div>
            <div style="grid-column: 1 / -1;"><strong>🌍 Pays:</strong> ${data.pays}</div>
          </div>
        </div>

        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #f3f4f6; text-align: center;">
          <span style="background: linear-gradient(135deg, #f97316, #ea580c); color: white; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 500;">
            🔥 Cliquez pour sélectionner
          </span>
        </div>
      </div>
    `;

    infoWindow.setContent(content);
    infoWindow.open(map, marker);
  };

  if (error) {
    return (
      <div className={`bg-orange-50 border border-orange-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center">
          <svg className="h-5 w-5 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-orange-700 text-sm">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Loader principal pour le chargement de la carte */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-lg">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Chargement de la carte et des logements...</p>
          </div>
        </div>
      )}

      {/* Loader pour la création/vérification de logement */}
      {isCreatingAccommodation && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 rounded-lg">
          <div className="bg-white rounded-lg p-6 shadow-lg text-center max-w-sm mx-4">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Traitement en cours...</h3>
            <p className="text-sm text-gray-600">Vérification et création du logement sélectionné.</p>
          </div>
        </div>
      )}

      {/* Tooltip léger pour le survol */}
      {tooltip.show && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-80 text-white px-3 py-2 rounded-lg text-sm font-medium z-50 pointer-events-none">
          {tooltip.content}
        </div>
      )}

      <div
        ref={mapRef}
        className="w-full h-96 rounded-lg border border-gray-200"
        style={{ minHeight: '400px' }}
      />

      {accommodationMarkers.length > 0 && (
        <div className="mt-3 text-center">
          <p className="text-sm text-gray-600">
            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs mr-2">
              🎓 École
            </span>
            <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
              🏠 {accommodationMarkers.length} logements disponibles
            </span>
          </p>
        </div>
      )}

      {/* GRILLE DE LOGEMENTS */}
      {(accommodationMarkers.length > 0 || parcLogements.length > 0) && (
        <div className="mt-6">
          {/* BOUTONS DE BASCULEMENT */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-1">
              <h3 className="text-lg font-semibold text-gray-900 mr-4">
                Logements disponibles
              </h3>
              <div className="bg-gray-100 rounded-lg p-1 flex">
                <button
                  onClick={() => setViewMode('generated')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === 'generated'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  Logements ({accommodationMarkers.length})
                </button>
                <button
                  onClick={() => {
                    setViewMode('parc');
                    if (parcLogements.length === 0) {
                      loadParcLogements();
                    }
                  }}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === 'parc'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  disabled={loadingParc}
                >
                  {loadingParc ? (
                    <span className="flex items-center">
                      <div className="animate-spin h-3 w-3 border border-gray-400 border-t-transparent rounded-full mr-1"></div>
                      Chargement...
                    </span>
                  ) : (
                    `Parc à logements (${parcLogements.length})`
                  )}
                </button>
              </div>
            </div>
            <span className="text-sm text-gray-500">
              {viewMode === 'generated'
                ? `${accommodationMarkers.length} résultat${accommodationMarkers.length > 1 ? 's' : ''}`
                : `${parcLogements.length} résultat${parcLogements.length > 1 ? 's' : ''}`
              }
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {viewMode === 'generated'
              ? accommodationMarkers.map((marker, index) => {
                  const data = marker.accommodationData;
                  const isSelected = marker.isSelected;

              return (
                <div
                  key={data.id}
                  className={`bg-white rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                    isSelected
                      ? 'border-orange-500 bg-orange-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => handleMarkerClick(marker, true)}
                >
                  {/* En-tête de la carte */}
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                      {data.title}
                    </h4>
                    <div className={`text-xs px-2 py-1 rounded-full ${
                      isSelected ? 'bg-orange-200 text-orange-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {data.distanceKm}km
                    </div>
                  </div>

                  {/* Prix */}
                  <div className="mb-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-gray-900">
                        {data.price}€
                      </span>
                      <span className="text-sm text-gray-600">
                        + {data.charges}€ charges
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Total: {data.price + data.charges}€/mois
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                    {data.description}
                  </p>

                  {/* Adresse */}
                  <div className="text-xs text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <span>📍</span>
                      <span className="truncate">{data.address}</span>
                    </div>
                    <div className="mt-1">
                      {data.ville}, {data.pays} - {data.code_postal}
                    </div>
                  </div>

                  {/* Bouton/Statut */}
                  <div className="flex items-center justify-between">
                    <div className={`text-xs px-3 py-1 rounded-full ${
                      isSelected
                        ? 'bg-orange-600 text-white'
                        : 'bg-blue-600 text-white'
                    }`}>
                      {isSelected ? '✓ Sélectionné' : 'Cliquer pour sélectionner'}
                    </div>
                    <div className="text-xs text-gray-400">
                      #{index + 1}
                    </div>
                  </div>
                </div>
              );
            })
              : parcLogements.map((logement, index) => {
                  const isSelected = selectedAccommodationId === logement.id;

                  return (
                    <div
                      key={logement.id}
                      className={`bg-white rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                        isSelected
                          ? 'border-orange-500 bg-orange-50 shadow-lg'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => handleParcLogementClick(logement)}
                    >
                      {/* En-tête de la carte */}
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                          {logement.title}
                        </h4>
                        <div className={`text-xs px-2 py-1 rounded-full ${
                          isSelected ? 'bg-orange-200 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          Parc
                        </div>
                      </div>

                      {/* Prix */}
                      <div className="mb-3">
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-bold text-gray-900">
                            {logement.price}€
                          </span>
                          <span className="text-sm text-gray-600">
                            + {logement.charges}€ charges
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Total: {logement.price + logement.charges}€/mois
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                        {logement.description}
                      </p>

                      {/* Adresse */}
                      <div className="text-xs text-gray-500 mb-3">
                        <div className="flex items-center gap-1">
                          <span>📍</span>
                          <span className="truncate">{logement.address}</span>
                        </div>
                        <div className="mt-1">
                          {logement.ville}, {logement.pays} - {logement.code_postal}
                        </div>
                      </div>

                      {/* Bouton/Statut */}
                      <div className="flex items-center justify-between">
                        <div className={`text-xs px-3 py-1 rounded-full ${
                          isSelected
                            ? 'bg-orange-600 text-white'
                            : 'bg-green-600 text-white'
                        }`}>
                          {isSelected ? '✓ Sélectionné' : 'Cliquer pour sélectionner'}
                        </div>
                        <div className="text-xs text-gray-400">
                          #{index + 1}
                        </div>
                      </div>
                    </div>
                  );
                })
            }
          </div>
        </div>
      )}

      {/* Modal de succès */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg max-w-md mx-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Succès !</h3>
              <p className="text-sm text-gray-600 mb-4">{successMessage}</p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'erreur */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg max-w-md mx-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Erreur</h3>
              <p className="text-sm text-gray-600 mb-4">{errorMessage}</p>
              <button
                onClick={() => setShowErrorModal(false)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapSelector;